import { useState } from "react";
import { Box, Button, Grid2 as Grid, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import { EditableGrid, EditorField } from "@/components/common/EditableGrid";
import { TabContent } from "@/components/common/PageLayout";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { ResourceName, Policy } from "@/types";
import { validateZipCode } from "@/utils/validators/inPlaceValidators";
import { useRecordContext } from "react-admin";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { PolicyNoteCell } from "@/components/common/PolicyNoteCell";
import { EffectiveWindowNote } from "./EffectiveWindowNote";
import { AccelFundingBanner } from "./AccelFundingBanner";
import { MonthlyEnrollmentSection } from "./MonthlyEnrollmentSection";
import { PolicyLinkedRecordsSection } from "./PolicyLinkedRecordsSection";
import { RxCarveOutSection } from "./RxCarveOutSection";
import ReportingCadenceDialog from "./ReportingCadenceDialog";
import { CADENCE_CHOICES, MONTH_DAY_CHOICES, WEEKDAY_CHOICES } from "./reportingCadenceConstants";

export const PolicyOverviewTab = ({ drawerExpanded }: { drawerExpanded: boolean }) => {
  // Two columns:
  //   Left  — Linked Records, Monthly Enrollment, Reporting Cadence
  //   Right — Overview, Specific Benefit, Aggregate Benefit (the detail grids)
  // The left column is intentionally narrower to give the detail grids more
  // breathing room. Collapses to a single column when the side drawer is open.
  const leftSize = { xs: 12, md: drawerExpanded ? 12 : 4 };
  const rightSize = { xs: 12, md: drawerExpanded ? 12 : 8 };

  return (
    <TabContent>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={leftSize}>
          <NotesSection />
          <PolicyLinkedRecordsSection />
          <RxCarveOutSection />
          <MonthlyEnrollmentSection />
          <ReportingCadenceSection isDrawerOpen={drawerExpanded} />
        </Grid>
        <Grid size={rightSize}>
          <ShowAccordionSection label="Overview" defaultExpanded>
            <AccelFundingBanner />
            <EditableGrid
              fields={policyOverviewFields}
              isDrawerOpen={drawerExpanded}
              recordType={ResourceName.POLICIES}
            />
          </ShowAccordionSection>
          <SpecificBenefitSection isDrawerOpen={drawerExpanded} />
          <AggregateBenefitSection isDrawerOpen={drawerExpanded} />
        </Grid>
      </Grid>
    </TabContent>
  );
};

// Policy-period summary Note (US 5370 / ADR-052 §10) — the same note shown on the
// client's Claims Summary header, surfaced here on the policy it belongs to.
const NotesSection = () => {
  const policyRecord = useRecordContext<Policy>();
  const { canEdit } = useResourcePermissions(ResourceName.POLICIES);
  if (!policyRecord?.id) return null;
  return (
    <ShowAccordionSection label="Notes" defaultExpanded>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1, color: "text.secondary" }}>
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" sx={{ fontStyle: "italic" }}>
          Also shown on the client&apos;s Claims Summary for this policy.
        </Typography>
      </Box>
      <PolicyNoteCell policyId={String(policyRecord.id)} note={policyRecord.note} canEdit={canEdit} block />
    </ShowAccordionSection>
  );
};

const SpecificBenefitSection = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const policyRecord = useRecordContext<Policy>();
  const sb = policyRecord?.specific_benefit;
  if (!sb?.is_specific_deductible && !sb?.specific_deductible) return null;

  return (
    <ShowAccordionSection label="Specific Benefit">
      <EffectiveWindowNote basis="specific" />
      <EditableGrid fields={specificBenefitFields} isDrawerOpen={isDrawerOpen} recordType={ResourceName.POLICIES} />
    </ShowAccordionSection>
  );
};

const AggregateBenefitSection = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const policyRecord = useRecordContext<Policy>();
  const ab = policyRecord?.aggregate_benefit;
  if (!ab?.is_aggregate_deductible && !ab?.aggregate_deductible) return null;

  return (
    <ShowAccordionSection label="Aggregate Benefit">
      <EffectiveWindowNote basis="aggregate" />
      <EditableGrid fields={aggregateBenefitFields} isDrawerOpen={isDrawerOpen} recordType={ResourceName.POLICIES} />
    </ShowAccordionSection>
  );
};

const reportingCadenceFields: EditorField[] = [
  {
    source: "reporting_cadence",
    type: "select",
    choices: CADENCE_CHOICES,
    // Switching cadence inline would 400 — the server requires cadence + its new
    // day field in one request. Use the "Change cadence" dialog instead.
    readOnlyReason: 'Use "Change cadence" to switch monthly/weekly',
  },
  // Day fields are part of the atomic cadence config — editing them inline (esp.
  // the off-cadence day, exposed via "show empty fields") would persist an invalid
  // cadence/day combo or 400. Read-only here; edits go through the "Change cadence" dialog.
  {
    source: "reporting_day_of_month",
    type: "select",
    choices: MONTH_DAY_CHOICES,
    readOnlyReason: 'Use "Change cadence" to edit',
  },
  {
    source: "reporting_day_of_week",
    type: "select",
    choices: WEEKDAY_CHOICES,
    readOnlyReason: 'Use "Change cadence" to edit',
  },
  { source: "reporting_start_date", type: "date" },
  { source: "task_lead_days", type: "number" },
];

const ReportingCadenceSection = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const policyRecord = useRecordContext<Policy>();
  const { canEdit } = useResourcePermissions(ResourceName.POLICIES);
  const [dialogOpen, setDialogOpen] = useState(false);
  if (!policyRecord?.id) return null;

  const hasCadence = !!policyRecord.reporting_cadence;

  return (
    <ShowAccordionSection label="Reporting Cadence">
      {hasCadence ? (
        <>
          <EditableGrid
            fields={reportingCadenceFields}
            isDrawerOpen={isDrawerOpen}
            recordType={ResourceName.POLICIES}
          />
          {canEdit && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Button size="small" onClick={() => setDialogOpen(true)}>
                Change cadence
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No reporting cadence configured — reporting reminder tasks are not generated for this policy.
          </Typography>
          {canEdit && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{ alignSelf: "flex-start" }}
            >
              Set up reporting cadence
            </Button>
          )}
        </Box>
      )}
      <ReportingCadenceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </ShowAccordionSection>
  );
};

export const policyOverviewFields: EditorField[] = [
  { source: "aegle_id" },

  // Policy details
  { source: "effective_start_date", type: "date" },
  { source: "effective_end_date", type: "date" },
  // #7320 / ADR-081 D5: mid-term cancellation date. Present only while the policy is
  // cancelled (the API couples the two), so it is shown only when set; the status
  // chip in the page header is where it gets created.
  // Read-only here (!4843 thread 29876): the in-place editor has no term bounds, so an
  // out-of-range date would 400 at the API; the status chip's dialog validates them.
  {
    source: "termination_date",
    type: "date",
    showWhen: "termination_date",
    readOnlyReason: "Use the status chip to change the termination date",
  },
  { source: "contract_basis", type: "contractBasis" },
  { source: "source_data.state_of_issuance", type: "state" },
  // Carrier policy number (#5433 / ADR-057) — lets a reviewer confirm which
  // carrier policy a reimbursement row resolved to. Editable via the policy form.
  { source: "source_data.stop_loss_carrier_policy_id" },

  // Policyholder info
  { source: "source_data.policyholder" },
  { source: "source_data.policyholder_contact_name" },
  { source: "source_data.policyholder_contact_email", type: "email" },
  { source: "source_data.policyholder_address" },
  { source: "source_data.policyholder_state", type: "state" },
  { source: "source_data.policyholder_zip_code", validate: validateZipCode },
  { source: "source_data.policyholder_affiliates" },

  // Benefit flags
  { source: "specific_benefit.is_specific_deductible", type: "boolean" },
  { source: "aggregate_benefit.is_aggregate_deductible", type: "boolean" },
  // is_accel_funding_enabled is deliberately NOT here — it renders as the
  // AccelFundingBanner above this grid. As a grid field it was hidden whenever
  // false (EditableGrid treats falsy as empty), which is most policies, since
  // the column is NOT NULL DEFAULT FALSE with backfill deferred.
  // Entity references (client, carrier, TPAs, Rx vendors) now render in the
  // "Linked Records" section (PolicyLinkedRecordsSection), not inline here.
];

const specificBenefitFields: EditorField[] = [
  // Coverage Details
  {
    source: "specific_benefit.has_medical_coverage",
    type: "boolean",
    section: "Coverage Details",
  },
  {
    source: "specific_benefit.has_rx_coverage",
    type: "boolean",
    section: "Coverage Details",
  },
  {
    source: "specific_benefit.contract_type",
    section: "Coverage Details",
  },
  {
    source: "specific_benefit.contract_basis",
    type: "contractBasis",
    section: "Coverage Details",
  },

  // Deductibles & Limits
  {
    source: "specific_benefit.is_specific_deductible",
    type: "boolean",
    section: "Deductibles & Limits",
  },
  {
    source: "specific_benefit.specific_deductible",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "specific_benefit.is_aggregate_specific_deductible",
    type: "boolean",
    section: "Deductibles & Limits",
  },
  {
    source: "specific_benefit.aggregate_specific_deductible",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "specific_benefit.specific_stop_loss_percentage_reimbursable",
    type: "percentage",
    section: "Deductibles & Limits",
  },
  {
    source: "specific_benefit.specific_maximum_reimbursement",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "specific_benefit.maximum_specific_benefit",
    type: "dollar",
    section: "Deductibles & Limits",
  },

  // Enrollment
  {
    source: "specific_benefit.covered_composite",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "specific_benefit.individuals_enrolled",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "specific_benefit.families_enrolled",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "specific_benefit.specific_employee_plus_children_enrolled",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "specific_benefit.specific_employee_plus_spouse_enrolled",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "specific_benefit.blended_enrolled",
    type: "number",
    section: "Enrollment",
  },

  // Premium Rates
  {
    source: "specific_benefit.specific_monthly_premium_rate",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "specific_benefit.specific_individual_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "specific_benefit.specific_family_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "specific_benefit.specific_employee_plus_spouse_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "specific_benefit.specific_employee_plus_children_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "specific_benefit.specific_composite_premium",
    type: "dollar",
    section: "Premium Rates",
  },

  // Eligible Expense Periods
  {
    source: "specific_benefit.eligible_expenses_incurred_start_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
  {
    source: "specific_benefit.eligible_expenses_incurred_end_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
  {
    source: "specific_benefit.eligible_expenses_paid_start_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
  {
    source: "specific_benefit.eligible_expenses_paid_end_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
];

const aggregateBenefitFields: EditorField[] = [
  // Coverage Details
  {
    source: "aggregate_benefit.has_medical_coverage",
    type: "boolean",
    section: "Coverage Details",
  },
  {
    source: "aggregate_benefit.has_rx_coverage",
    type: "boolean",
    section: "Coverage Details",
  },
  {
    source: "aggregate_benefit.contract_type",
    section: "Coverage Details",
  },
  {
    source: "aggregate_benefit.contract_basis",
    type: "contractBasis",
    section: "Coverage Details",
  },

  // Deductibles & Limits
  {
    source: "aggregate_benefit.is_aggregate_deductible",
    type: "boolean",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.aggregate_deductible",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.aggregate_stop_loss_individual_claim_limit",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.aggregate_stop_loss_percentage_reimbursable",
    type: "percentage",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.maximum_annual_reimbursement",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.aggregate_individual_monthly_factor",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.aggregate_employee_plus_children_monthly_factor",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.aggregate_employee_plus_spouse_monthly_factor",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.aggregate_family_monthly_factor",
    type: "dollar",
    section: "Deductibles & Limits",
  },
  {
    source: "aggregate_benefit.monthly_aggregate_spend_per_composite",
    type: "dollar",
    section: "Deductibles & Limits",
  },

  // Enrollment
  {
    source: "aggregate_benefit.covered_composite",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "aggregate_benefit.individuals_enrolled",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "aggregate_benefit.families_enrolled",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "aggregate_benefit.aggregate_employee_plus_children_enrolled",
    type: "number",
    section: "Enrollment",
  },
  {
    source: "aggregate_benefit.aggregate_employee_plus_spouse_enrolled",
    type: "number",
    section: "Enrollment",
  },

  // Premium Rates
  {
    source: "aggregate_benefit.aggregate_individual_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "aggregate_benefit.aggregate_family_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "aggregate_benefit.aggregate_employee_plus_spouse_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "aggregate_benefit.aggregate_employee_plus_children_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "aggregate_benefit.aggregate_blended_premium",
    type: "dollar",
    section: "Premium Rates",
  },
  {
    source: "aggregate_benefit.aggregate_composite_premium",
    type: "dollar",
    section: "Premium Rates",
  },

  // Eligible Expense Periods
  {
    source: "aggregate_benefit.eligible_expenses_incurred_start_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
  {
    source: "aggregate_benefit.eligible_expenses_incurred_end_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
  {
    source: "aggregate_benefit.eligible_expenses_paid_start_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
  {
    source: "aggregate_benefit.eligible_expenses_paid_end_date",
    type: "date",
    section: "Eligible Expense Periods",
  },
];
